import { useMemo } from 'react';
import type { Measure } from '../../../../../api/work-periods-client/work-periods-client.type';
import { deduplicateItems } from '../../../../../helpers/general-helpers';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import { useSelectedMonthlyAveragesMeasures } from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import {
  setSelectedBoards,
  setSelectedPortfolios,
  setSelectedTeams,
} from '../../../../../store/process-analysis-store/process-analysis-store.actions';
import { Tab } from '../../../process-analysis.type';
import type { MonthlyAveragesTableRow } from './table.type';

/**
 * Custom hook that returns available entities for selection based on the active tab
 * Filters entities by current organization similar to multiselect components
 *
 * @returns {Array} Array of entities with id and name properties
 */
const useAvailableEntities = (): Array<{ id: string; name: string }> => {
  const currentOrganization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) =>
    deduplicateItems(state.portfolios.filter((p) => p.organization.id === currentOrganization?.id)),
  );
  const teams = useGlobalStore((state) => deduplicateItems(state.teams || []));

  const activeTab = useProcessAnalysisStore((state) => state.activeTab);

  const entities: Array<{ id: string; name: string }> = [];

  switch (activeTab) {
    case Tab.Portfolios:
      // Add global portfolio if it exists and belongs to current organization
      if (portfolio && portfolio.organization.id === currentOrganization?.id) {
        entities.push({
          id: portfolio.id,
          name: portfolio.name,
        });
      }

      // Add available portfolios (excluding global portfolio to avoid duplication)
      portfolios.forEach((p) => {
        if (p.id !== portfolio?.id) {
          entities.push({
            id: p.id,
            name: p.name,
          });
        }
      });
      break;

    case Tab.Teams:
      teams.forEach((team) => {
        entities.push({
          id: team.id,
          name: team.name,
        });
      });
      break;

    case Tab.Boards:
      // Get boards from teams' subprojects, filtered by current organization
      teams.forEach((team) => {
        team.subprojects?.forEach((board) => {
          entities.push({
            id: board.id,
            name: board.name,
          });
        });
      });
      break;

    default:
      break;
  }

  return entities;
};

/**
 * Custom hook that returns selected entities and setter function based on active tab
 *
 * @returns {Object} An object containing selectedEntities array and setSelectedEntities function
 */
const useSelectedEntities = () => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  const selectedEntities = (() => {
    switch (activeTab) {
      case Tab.Portfolios:
        return selectedPortfolios;
      case Tab.Teams:
        return selectedTeams;
      case Tab.Boards:
        return selectedBoards;
      default:
        return [];
    }
  })();

  const setSelectedEntities = (() => {
    switch (activeTab) {
      case Tab.Portfolios:
        return setSelectedPortfolios;
      case Tab.Teams:
        return setSelectedTeams;
      case Tab.Boards:
        return setSelectedBoards;
      default:
        return () => {};
    }
  })();

  return {
    selectedEntities,
    setSelectedEntities,
  };
};

/**
 * Custom hook that filters monthly averages table data based on selected measures
 * Only includes groups that have at least one selected measure
 *
 * @param {Record<string, MonthlyAveragesTableRow[]>} transformedData - The transformed table data grouped by measure groups
 * @returns {Record<string, MonthlyAveragesTableRow[]>} Filtered data with only selected measures and non-empty groups
 */
const useFilteredMonthlyAveragesData = (
  transformedData: Record<string, MonthlyAveragesTableRow[]>,
): Record<string, MonthlyAveragesTableRow[]> => {
  const selectedMonthlyAveragesMeasures = useSelectedMonthlyAveragesMeasures();

  const filteredData = useMemo(() => {
    const filtered: Record<string, MonthlyAveragesTableRow[]> = {};

    Object.entries(transformedData).forEach(([groupName, rows]) => {
      const filteredRows = rows.filter((row) => selectedMonthlyAveragesMeasures.includes(row.measureName as Measure));

      // Only include the group if it has at least one selected measure
      if (filteredRows.length > 0) {
        filtered[groupName] = filteredRows;
      }
    });

    return filtered;
  }, [transformedData, selectedMonthlyAveragesMeasures]);

  return filteredData;
};

export { useAvailableEntities, useFilteredMonthlyAveragesData, useSelectedEntities };
